import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/#method" className="text-sm text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors">
              The Method
            </Link>
            <Link to="/about" className="text-sm text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors">
              About
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-6 animate-fade-up opacity-0"
             style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            About Calm Scale™
          </p>
          
          <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.2] mb-8 animate-fade-up opacity-0" 
              style={{ fontFamily: 'Playfair Display, serif', animationDelay: '200ms', animationFillMode: 'forwards' }}>
            A structured alternative to hustle-driven online business — designed for thoughtful founders who want profitable digital assets without constant visibility.
          </h1>
          
          <div className="animate-fade-up opacity-0" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            <Link to="/checkout">
              <Button 
                variant="ghost"
                className="text-[#6B8E7C] hover:bg-[#6B8E7C]/5 text-sm px-0"
                data-testid="hero-soft-cta"
              >
                Begin My 14-Day Build
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 1: THE ORIGIN */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Origin
          </p>
          <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Why Calm Scale Exists
          </h2>

          <div className="space-y-6 text-[#2C2C2C]/70 font-light leading-relaxed">
            <p>
              Calm Scale™ was created for founders who don't resonate with loud marketing, constant posting, or performance-driven growth models — but still want scalable income.
            </p>
            
            <p>
              It was built from the same structure I use to create digital products: clarity first, assets second, visibility last.
            </p>
            
            <p className="text-[#2C2C2C]/80">
              Over time, one pattern became clear:
            </p>
            
            <p className="text-[#2C2C2C] font-normal">
              Most capable women weren't lacking ambition.
              <br />
              They were lacking structure.
            </p>
            
            <div className="py-4">
              <p className="text-[#2C2C2C]/70">They had ideas.</p>
              <p className="text-[#2C2C2C]/70">They had skill.</p>
              <p className="text-[#2C2C2C]/70">They had insight.</p>
            </div>
            
            <p>
              What they didn't have was a model designed for how they naturally build.
            </p>
            
            <p className="text-[#6B8E7C] font-normal text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calm Scale™ is that model.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="h-px bg-[#E5E0D6]" />
      </div>

      {/* SECTION 2: MARKET OBSERVATION */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            Market Observation
          </p>
          <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            The Problem With Most Online Business Models
          </h2>

          <div className="space-y-6 text-[#2C2C2C]/70 font-light leading-relaxed">
            <p>
              Most online growth advice is built for:
            </p>
            
            <ul className="space-y-2 pl-1">
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>High visibility</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>High energy output</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>High content volume</span>
              </li>
            </ul>
            
            <p className="text-[#2C2C2C] font-normal">
              It rewards noise over depth.
            </p>
            
            <p>
              For thoughtful, introverted founders, this creates friction — not leverage.
            </p>
            
            <p className="pt-4 text-[#2C2C2C]/80">
              Calm Scale™ was intentionally designed as an alternative.
            </p>
            
            <div className="py-4">
              <p className="text-[#2C2C2C]">Not louder.</p>
              <p className="text-[#2C2C2C]">More structured.</p>
            </div>
            
            <div className="space-y-2">
              <p className="text-[#2C2C2C]">No gamification.</p>
              <p className="text-[#2C2C2C]">No streaks.</p>
              <p className="text-[#2C2C2C]">No endless experimentation.</p>
            </div>
            
            <p className="text-[#6B8E7C] font-normal pt-2">
              Just structured execution.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="h-px bg-[#E5E0D6]" />
      </div>

      {/* SECTION 3: FOUNDER AUTHORITY */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            Founder Authority
          </p>
          <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            How I Build
          </h2>

          <div className="space-y-6 text-[#2C2C2C]/70 font-light leading-relaxed">
            <p>
              I build digital products the same way Calm Scale™ is structured:
            </p>
            
            <ul className="space-y-2 pl-1">
              <li className="flex items-start gap-3">
                <span className="text-[#6B8E7C] mt-1">•</span>
                <span>Define the offer with precision</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#6B8E7C] mt-1">•</span>
                <span>Build the asset intentionally</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#6B8E7C] mt-1">•</span>
                <span>Position it strategically</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#6B8E7C] mt-1">•</span>
                <span>Launch with clarity</span>
              </li>
            </ul>
            
            <div className="py-4">
              <p className="text-[#2C2C2C]/70">Not through volume.</p>
              <p className="text-[#2C2C2C]/70">Not through virality.</p>
              <p className="text-[#2C2C2C] font-normal">Through structure.</p>
            </div>
            
            <p>
              This method was refined through real product builds, iteration, and disciplined simplification — then distilled into a repeatable 14-day framework.
            </p>
            
            <div className="py-4">
              <p className="text-[#2C2C2C]/70">Not to accelerate noise.</p>
              <p className="text-[#2C2C2C]">To accelerate clarity.</p>
            </div>
            
            <p className="text-[#6B8E7C] font-normal text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calm. Focused. Profitable.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="h-px bg-[#E5E0D6]" />
      </div>

      {/* SECTION 4: WHAT CALM SCALE IS */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            Clarity
          </p>
          <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            What This Is — And What It Isn't
          </h2>

          <div className="space-y-6 text-[#2C2C2C]/70 font-light leading-relaxed">
            <p>
              Calm Scale™ is not:
            </p>
            
            <ul className="space-y-2 pl-1">
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>A motivational program</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>A content challenge</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#2C2C2C]/30 mt-1">•</span>
                <span>A passive prompt library</span>
              </li>
            </ul>
            
            <p className="pt-4">
              It is a structured build environment designed to help you create a profitable digital asset that does not depend on your daily presence.
            </p>
            
            <div className="py-4 border-l-2 border-[#6B8E7C]/30 pl-6">
              <p className="text-[#2C2C2C]/80 mb-2">
                This isn't about becoming louder.
              </p>
              <p className="text-[#6B8E7C] font-normal">
                It's about becoming leveraged.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: PHILOSOPHY CLOSE */}
      <section className="py-20 px-6 bg-[#F2F0EB]/40">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
            The Philosophy
          </p>
          
          <div className="space-y-2 mb-8">
            <p className="text-2xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Build once.
            </p>
            <p className="text-2xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Refine strategically.
            </p>
            <p className="text-2xl text-[#6B8E7C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Earn repeatedly.
            </p>
          </div>
          
          <div className="space-y-4 text-[#2C2C2C]/70 font-light leading-relaxed">
            <p>
              Calm Scale™ exists for founders who value depth over noise and leverage over visibility.
            </p>
            
            <p className="text-[#2C2C2C]/80">
              If that resonates, you're in the right place.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to build something that lasts?
          </h2>
          
          <Link to="/checkout">
            <Button 
              className="btn-primary text-base px-10 py-4"
              data-testid="final-cta-btn"
            >
              Begin My 14-Day Build
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          
          <p className="text-xs text-[#2C2C2C]/50 mt-4">
            Secure checkout via PayPal. Instant access upon confirmation.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#E5E0D6]">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-xs text-[#2C2C2C]/50 hover:text-[#6B8E7C] transition-colors">
              Home
            </Link>
            <Link to="/about" className="text-xs text-[#2C2C2C]/50 hover:text-[#6B8E7C] transition-colors">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
